import {
  Alert,
  Card,
  Col,
  Empty,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTriangleExclamation,
  faUserSecret,
} from "@fortawesome/free-solid-svg-icons";
import { getIncidents } from "../repository/data.repository";
import {
  Incident,
  IncidentSeverity,
  IncidentStatus,
  MAX_PAGE_SIZE,
} from "../repository/data.default";
import { getProblemMessage, retry } from "../utils/http";

// PD-02 §6.16.8 — company managers monitor incidents reported in their
// company. Read-only here (admins triage + follow-ups). Anonymous reports
// have their reporter identity suppressed; the report body still shows.
function IncidentsPage() {
  const [items, setItems] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    retry(() =>
      getIncidents({
        searchQuery: { conditions: [] },
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
        sortBy: "reportedDate",
        sortDescending: true,
      }),
    )
      .then((d) => setItems(d.data ?? []))
      .catch((err) =>
        message.error(getProblemMessage(err) || "Failed to load incidents."),
      )
      .finally(() => setLoading(false));
  }, []);

  const open = items.filter(
    (i) =>
      i.status === IncidentStatus.Open || i.status === IncidentStatus.InProgress,
  ).length;
  const highSeverity = items.filter(
    (i) =>
      i.severity === IncidentSeverity.High ||
      i.severity === IncidentSeverity.Critical,
  ).length;

  const severityColor: Record<IncidentSeverity, string> = {
    [IncidentSeverity.Low]: "blue",
    [IncidentSeverity.Medium]: "orange",
    [IncidentSeverity.High]: "volcano",
    [IncidentSeverity.Critical]: "red",
  };
  const statusColor: Record<IncidentStatus, string> = {
    [IncidentStatus.Open]: "red",
    [IncidentStatus.InProgress]: "processing",
    [IncidentStatus.Resolved]: "green",
    [IncidentStatus.Closed]: "default",
  };

  const columns: ColumnsType<Incident> = [
    {
      title: "Reported",
      dataIndex: "reportedDate",
      width: 150,
      render: (v: string) => dayjs(v).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "Title",
      dataIndex: "title",
      ellipsis: true,
    },
    {
      title: "Severity",
      dataIndex: "severity",
      width: 110,
      render: (s: IncidentSeverity) => (
        <Tag color={severityColor[s]}>{s}</Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 130,
      render: (s: IncidentStatus) => (
        <Tag color={statusColor[s]}>{s}</Tag>
      ),
    },
    {
      title: "Reporter",
      dataIndex: "reportedByUserUserName",
      width: 180,
      render: (name: string | null, row) =>
        row.isAnonymous ? (
          <Space>
            <FontAwesomeIcon icon={faUserSecret} />
            <Typography.Text type="secondary">Anonymous</Typography.Text>
          </Space>
        ) : (
          name ?? <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    { title: "Department", dataIndex: "departmentName", width: 160 },
    { title: "Category", dataIndex: "incidentTypeName", width: 140 },
  ];

  return (
    <Col className="animate__animated animate__fadeIn flex flex-auto flex-col gap-4 overflow-y-auto p-4">
      <Row>
        <Typography.Title level={3} className="!m-0">
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            className="mr-2 text-coral"
            style={{ color: "#f06364" }}
          />
          Incidents
        </Typography.Title>
      </Row>

      <Alert
        type="info"
        showIcon
        message="Read-only monitoring"
        description="Reports from your employees — triage, assignment and follow-up actions are managed by the platform operator. Anonymous reports suppress reporter identity; the report body is still fully visible."
      />

      <Row gutter={16}>
        <Col flex="1 1 160px">
          <Card bodyStyle={{ padding: 16 }}>
            <Space direction="vertical">
              <Typography.Text type="secondary" className="text-xs uppercase">
                Open
              </Typography.Text>
              <Typography.Title level={2} className="!m-0">
                {open}
              </Typography.Title>
            </Space>
          </Card>
        </Col>
        <Col flex="1 1 160px">
          <Card bodyStyle={{ padding: 16 }}>
            <Space direction="vertical">
              <Typography.Text type="secondary" className="text-xs uppercase">
                High / Critical
              </Typography.Text>
              <Typography.Title level={2} className="!m-0" style={{ color: "#f06364" }}>
                {highSeverity}
              </Typography.Title>
            </Space>
          </Card>
        </Col>
        <Col flex="1 1 160px">
          <Card bodyStyle={{ padding: 16 }}>
            <Space direction="vertical">
              <Typography.Text type="secondary" className="text-xs uppercase">
                Total
              </Typography.Text>
              <Typography.Title level={2} className="!m-0">
                {items.length}
              </Typography.Title>
            </Space>
          </Card>
        </Col>
      </Row>

      {loading ? (
        <Card>
          <Spin />
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <Empty description="No incidents reported in your company yet." />
        </Card>
      ) : (
        <Card bodyStyle={{ padding: 0 }}>
          <Table
            rowKey="uid"
            size="small"
            dataSource={items}
            columns={columns}
            pagination={{ pageSize: 50, showSizeChanger: false }}
            expandable={{
              expandedRowRender: (row) => (
                <div style={{ padding: 12 }}>
                  <Typography.Text strong>Description:</Typography.Text>
                  <Typography.Paragraph className="!mt-1 whitespace-pre-wrap">
                    {row.description}
                  </Typography.Paragraph>
                  {row.assignedToUserUserName && (
                    <Typography.Text type="secondary" className="text-xs">
                      Assigned to: {row.assignedToUserUserName}
                    </Typography.Text>
                  )}
                </div>
              ),
            }}
          />
        </Card>
      )}
    </Col>
  );
}

export default IncidentsPage;
